import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardDescription, CardHeader } from '../components/ui/card';
import { CheckCircle2, Settings, Cpu, Package, FileText, Upload, Wrench, Layers, Gauge, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { cncTranslations } from '../translations/cnc';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Bitrix24Modal from '../components/Bitrix24Modal';
import GalleryLightbox from '../components/GalleryLightbox';
import CertificationBadges from '../components/CertificationBadges';

/**
 * CNCPage Component
 * 
 * Landing page for CNC Machining services.
 * Showcases capabilities, materials, applications, and competitive advantages.
 * 
 * Sections:
 * - Hero: Main value proposition
 * - Materials: G10, FR4, phenolics, aluminum, engineering plastics
 * - Mid CTA: Quote request
 * - Gallery: Examples of machined parts
 * - Capabilities: CNC machining capabilities
 * - Applications: Use cases
 * - Advantages: Competitive differentiators
 * - Final CTA: Contact information
 * - Footer: Company info and locations
 */
const CNCPage = () => {
  const { language } = useLanguage();
  const t = cncTranslations[language];
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  return (
    <div className="bg-black min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/cnc/hero.jpg"
            alt="CNC Precision Parts"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/85 to-black/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center md:text-left">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-[#B91C1C]/20 border border-[#B91C1C]/50 rounded-full mb-6 backdrop-blur-sm">
              <span className="text-[#B91C1C] text-sm font-semibold">{t.hero.badge}</span>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t.hero.title1} <span className="text-[#B91C1C]">{t.hero.titleCNC}</span> {t.hero.title2}{' '}
              <span className="text-[#B91C1C]">{t.hero.titleMaterials}</span> {t.hero.titleAnd}{' '}
              <span className="text-[#B91C1C]">{t.hero.titlePhenolic}</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-gray-300 mb-10 leading-relaxed">
              {t.hero.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => document.getElementById('cta').scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-8 py-6 text-lg font-semibold  shadow-lg shadow-[#B91C1C]/30"
              >
                <FileText className="mr-2" size={20} />
                {t.hero.cta1}
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap gap-8 text-gray-400 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#B91C1C] rounded-full"></div>
                <span>{t.hero.trust1}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#B91C1C] rounded-full"></div>
                <span>{t.hero.trust2}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#B91C1C] rounded-full"></div>
                <span>{t.hero.trust3}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-[#B91C1C] rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Trust Signals - Certification Badges */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <CertificationBadges badges={['iso']} />
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section className="py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, #3B82F6 2px, #3B82F6 4px),
                             repeating-linear-gradient(90deg, transparent, transparent 2px, #3B82F6 2px, #3B82F6 4px)`
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.materials.title} <span className="text-[#B91C1C]">{t.materials.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Technical Materials */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-8 backdrop-blur-sm ">
              <h3 className="text-2xl font-bold text-[#B91C1C] mb-6">{t.materials.technical.title}</h3>
              <ul className="space-y-4">
                {t.materials.technical.items.map((material, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="text-[#B91C1C] flex-shrink-0" size={20} />
                    <span className="text-gray-300 text-lg">{material}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Standard Materials */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-8 backdrop-blur-sm ">
              <h3 className="text-2xl font-bold text-[#B91C1C] mb-6">{t.materials.standard.title}</h3>
              <ul className="space-y-4">
                {t.materials.standard.items.map((material, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="text-[#B91C1C] flex-shrink-0" size={20} />
                    <span className="text-gray-300 text-lg">{material}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Note */}
          <div className="mt-12 bg-gradient-to-r from-blue-900/20 to-transparent border-l-4 border-[#B91C1C] p-6 rounded-r-lg">
            <p className="text-gray-300 text-lg leading-relaxed">
              <span className="font-semibold text-[#B91C1C]">{t.materials.note}</span> {t.materials.noteText}
            </p>
          </div>
        </div>
      </section>

      {/* Mid CTA Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1713371398484-cc4e4f6a262a"
            alt="CNC Cutting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.midCta.title} <span className="text-[#B91C1C]">{t.midCta.titleQuote}</span> {t.midCta.titleOr}{' '}
            <span className="text-[#B91C1C]">{t.midCta.titleSupport}</span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            {t.midCta.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => document.getElementById('cta').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#B91C1C]/40"
            >
              <FileText className="mr-2" size={22} />
              {t.midCta.button1}
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.gallery.title} <span className="text-[#B91C1C]">{t.gallery.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <GalleryLightbox 
            images={[
              {
                src: '/images/cnc/gallery1.jpg',
                alt: 'CNC Machining Example 1',
                title: t.gallery.images[0].title,
                caption: t.gallery.images[0].caption
              },
              {
                src: '/images/cnc/gallery2.jpg',
                alt: 'CNC Machining Example 2',
                title: t.gallery.images[1].title,
                caption: t.gallery.images[1].caption
              },
              {
                src: '/images/cnc/gallery3.jpg',
                alt: 'CNC Machining Example 3',
                title: t.gallery.images[2].title,
                caption: t.gallery.images[2].caption
              }
            ]}
            onQuoteClick={() => setIsModalOpen(true)}
            whatsappMessage={language === 'en' 
              ? 'Hello, I would like to request a quote for CNC machining services similar to the examples I saw in your gallery.'
              : 'Hola, me gustaría solicitar una cotización para servicios de maquinado CNC similar a los ejemplos que vi en su galería.'
            }
          />
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.capabilities.title} <span className="text-[#B91C1C]">{t.capabilities.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Settings, text: t.capabilities.items[0] },
              { icon: Cpu, text: t.capabilities.items[1] },
              { icon: Layers, text: t.capabilities.items[2] },
              { icon: Gauge, text: t.capabilities.items[3] },
              { icon: Wrench, text: t.capabilities.items[4] },
              { icon: Package, text: t.capabilities.items[5] }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card 
                  key={index}
                  className="bg-gray-800/50 border-gray-700  backdrop-blur-sm"
                >
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#B91C1C]/20 to-gray-900 rounded-lg flex items-center justify-center ">
                        <IconComponent className="text-[#B91C1C]" size={24} />
                      </div>
                      <CardDescription className="text-gray-300 text-base leading-relaxed ">
                        {item.text}
                      </CardDescription>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.applications.title} <span className="text-[#B91C1C]">{t.applications.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.applications.items.map((application, index) => (
              <div 
                key={index}
                className="bg-gray-800/50 border border-gray-700 rounded-lg p-6  backdrop-blur-sm"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="text-[#B91C1C] group-hover:scale-110 transition-transform duration-300" size={24} />
                  </div>
                  <p className="text-gray-300 text-base leading-relaxed ">
                    {application}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose DSI Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.advantages.title} <span className="text-[#B91C1C]">{t.advantages.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.advantages.items.map((advantage, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 bg-gray-900/50 border border-gray-800 rounded-lg p-6  backdrop-blur-sm"
              >
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle2 className="text-[#B91C1C] group-hover:scale-110 transition-transform duration-300" size={24} />
                </div>
                <p className="text-gray-300 text-base leading-relaxed ">
                  {advantage}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="cta" className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.finalCta.title}{' '}
            <span className="text-[#B91C1C]">{t.finalCta.titleAccent}</span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            {t.finalCta.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => setIsModalOpen(true)}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#B91C1C]/40"
            >
              <FileText className="mr-2" size={22} />
              {t.finalCta.button1}
            </Button>
            <Button 
              size="lg"
              onClick={() => window.open('https://wa.me/526181719959?text=Hola,%20me%20gustaría%20solicitar%20una%20cotización%20para%20maquinado%20CNC', '_blank')}
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#25D366]/40"
            >
              <MessageCircle className="mr-2" size={22} />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Footer with Contact Section */}
      <Footer />

      {/* Bitrix24 Modal */}
      <Bitrix24Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default CNCPage;